import * as Highcharts from 'highcharts';

/**
 * Shared helpers reused across the home dashboard's edit modals (modal.ts, gym-modal.ts,
 * runs-modal.ts, stairmaster-modal.ts) and home.ts's post-import transactions redraw.
 *
 * Extracted verbatim (findChart/getPointIndex) or generalized (updateChartTotals) from
 * src/js/home/modal.ts per Phase 220 Plan 03 Task 1 — see 220-PATTERNS.md § shared.ts.
 */

/** Locate a live Highcharts instance by its container element id. Verbatim from modal.ts:260. */
export function findChart(chartId: string): Highcharts.Chart | undefined {
    //@ts-ignore
    return Highcharts.charts.find((c: any) => c?.renderTo?.id === chartId);
}

/**
 * Resolve a date string to its zero-based index within a series' pointStart/pointInterval
 * (one day) sequence. Verbatim from modal.ts:252.
 */
export function getPointIndex(series: Highcharts.Series, date: string): number {
    //@ts-ignore
    const pointStart = series.options.pointStart as number;
    // Use local time to match pointStart which is Date.parse(dates_array[0]) (local)
    const clickedTimestamp = Date.parse(date);
    return Math.round((clickedTimestamp - pointStart) / 86400000);
}

/**
 * Swap a chart's titlebar totals (`.chart-total-value` spans inside its `.chart-drag-handle`)
 * from a fresh server-computed payload — no page reload, no chart re-render.
 *
 * Generalized from modal.ts's `updateSnapTotals` (the shipped MODAL-05 reference pattern):
 * the hardcoded `.chart-wrapper[data-id="snap"]` selector is parametrized into `chartKey`.
 * `totals` is always a FLAT `[{name,value}]` list — callers holding a keyed/nested payload
 * (e.g. the weight/waist save's `{weight:[...], waist:[...]}`) must call this once per key
 * with each sub-list, never pass the wrapper object itself.
 *
 * Writes via `.textContent` only (never `innerHTML`) — T-220-05: values are server-side
 * `number_format`/string-concatenated strings, so no markup-injection surface exists even
 * though they originate from a network response.
 */
export function updateChartTotals(
    chartKey: string,
    totals: Array<{ name: string; value: string }> | undefined,
): void {
    const wrapper = document.querySelector(`.chart-wrapper[data-id="${chartKey}"]`);
    if (!wrapper || !totals) return;
    wrapper.querySelectorAll('.chart-total').forEach((t) => {
        const label = t.querySelector('.chart-total-label')?.textContent?.trim().replace(/:$/, '');
        const valEl = t.querySelector('.chart-total-value');
        if (!valEl) return;
        const match = totals.find((x) => x.name === label);
        if (match) valEl.textContent = String(match.value);
    });
}

/**
 * Active-stepper registry (Phase 262, HOME-01/02) — the indirection the shared date-nav
 * control needs to act on whichever click-to-edit modal happens to be open.
 *
 * (a) The five modal modules (modal.ts, gym-modal.ts, runs-modal.ts, stairmaster-modal.ts,
 *     mood-modal.ts) have zero cross-module state and no mutual visibility today — each keeps
 *     its own private currentDate (and modal.ts alone also keeps currentChartType). A date-nav
 *     control built once in the shared #chart-edit-modal header (P-01) therefore cannot know
 *     which open*Modal() is currently active, or how to step its date, without this registry
 *     sitting between them.
 * (b) date-nav.ts must never import a modal module directly. Doing so would duplicate
 *     home.ts's updateValue() dispatcher and create a second place that has to track the
 *     editable-type allowlist (EDITABLE_CHART_IDS) — this registry is the one dispatch point
 *     instead.
 * (c) The sync hook (registerDateNavSync) exists so a modal module is never responsible for
 *     touching header DOM (the date input's value, the next-arrow's disabled state) — it only
 *     ever calls setActiveStepper(), and the header updates itself.
 */

/** A modal's date-step implementation: reload its body for `newDate`, then report success/failure. */
export type ModalDateStepper = (newDate: string, done: (ok: boolean) => void) => void;

let _activeDate = '';
let _activeStep: ModalDateStepper | null = null;
let _dateNavSync: ((date: string) => void) | null = null;

/**
 * Called by each open*Modal(date) immediately before showing the modal, and again by
 * date-nav.ts itself before invoking a step. Records the active date/callback pair and
 * notifies the date-nav sync hook, if one is registered (it is absent on any page where
 * initDateNav() never ran, since shared.ts is imported on every page).
 */
export function setActiveStepper(date: string, fn: ModalDateStepper | null): void {
    _activeDate = date;
    _activeStep = fn;
    _dateNavSync?.(date);
}

/** Returns the currently active date/stepper pair, or null when no modal has registered one. */
export function getActiveStepper(): { date: string; step: ModalDateStepper } | null {
    if (!_activeStep) return null;
    return { date: _activeDate, step: _activeStep };
}

/** Registers date-nav.ts's header-sync callback. Called once from initDateNav(). */
export function registerDateNavSync(fn: (date: string) => void): void {
    _dateNavSync = fn;
}

/**
 * Estimate-bar styles (GRAPH-01, Phase 279 Plan 02) — one definition per chart family, so
 * `home.ts` (initial render) and `modal.ts` (post-save refresh) can never silently diverge.
 *
 * Operator verdict at the 279-07 checkpoint (2026-09-03) superseded the UI-SPEC's original
 * coral/turquoise gradients: coffee and weed estimate bars must read as "the same bar, a
 * little softer" — a FLAT fill of the series' own hue at reduced alpha, no gradient. The
 * top/bottom amber-capped gradient is alcohol's alone. The dashed 1px border applied by the
 * call sites is what marks a bar as an estimate on every family.
 */
const ALC_COLOR_ESTIMATE: Highcharts.GradientColorObject = {
    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
    stops: [
        [0, '#E1A818'],
        [0.1, 'rgba(59, 171, 211, 0.45)'],
        [0.9, 'rgba(59, 171, 211, 0.45)'],
        [1, '#E1A818'],
    ],
};

// Flat fills. The hex bases mirror HomeModel::getGraphDataArray() 'colors' for each series
// (coffee #8B5E3C; weed puffs #4CAF50, weed THC-drinks #8BC34A) at ESTIMATE_ALPHA.
const ESTIMATE_ALPHA = 0.55;
const COFFEE_COLOR_ESTIMATE = `rgba(139, 94, 60, ${ESTIMATE_ALPHA})`;
const WEED_COLOR_ESTIMATE: readonly string[] = [
    `rgba(76, 175, 80, ${ESTIMATE_ALPHA})`,   // series 0: puffs      (#4CAF50)
    `rgba(139, 195, 74, ${ESTIMATE_ALPHA})`,  // series 1: THC-drinks (#8BC34A)
];

/**
 * Returns the estimate-bar fill for a chart family. D-06: 'weed' covers BOTH weed sub-series
 * (puffs and THC-drinks) under one flag; `seriesIndex` picks which green so each bar softens
 * toward ITS OWN colour. Falls back to the alcohol gradient for 'alc' and any future
 * estimate-flagged key.
 */
export function estimateStyleFor(chartKey: string, seriesIndex = 0): string | Highcharts.GradientColorObject {
    if (chartKey === 'coffee') return COFFEE_COLOR_ESTIMATE;
    if (chartKey === 'weed') return WEED_COLOR_ESTIMATE[seriesIndex] ?? WEED_COLOR_ESTIMATE[0];
    return ALC_COLOR_ESTIMATE;
}
